import { useCallback, useEffect, useState } from 'react';
import { useStytchUser } from '@stytch/react';

import { credentialErrorCategory } from 'features/authentication/SignIn/helpers';
import { ErrorTrackingProps } from 'features/authentication/SignIn/types';

import { toI18n } from 'util/i18n';
import { browserHistory } from 'util/router';
import { useStytch } from 'util/stytch';
import { EVENT_ACTIONS, TRACK_ACTION_TYPES } from 'util/tracking_constants';
import { useExtendUx, useTrackUx } from 'util/uxEvents';

import CodeForm from '../CodeForm';
import {
  MfaVariant,
  ROOT_PATH,
  SIGN_IN_PATH,
  TOO_MANY_REQUESTS_ERROR,
} from '../constants';

export type Props = {
  onSuccess: () => void;
} & ErrorTrackingProps;

const MfaForm = ({ onError, onSuccess }: Props) => {
  const [alertMessage, setAlertMessage] = useState('');
  const [phoneId, setPhoneId] = useState('');
  const trackUx = useTrackUx();
  const extendUx = useExtendUx();

  const { user } = useStytchUser();
  const stytchClient = useStytch();

  const phone = user?.phone_numbers[0]?.phone_number as string;

  extendUx({
    view: MfaVariant.SMS,
  });

  const sendCode = useCallback(() => {
    stytchClient
      .sendMfaSmsOtp(phone)
      .then(({ method_id }) => {
        setPhoneId(method_id);
      })
      .catch(error => {
        const errorType = credentialErrorCategory(error);
        let errorMessage = toI18n(
          'authentication.errors.initial_send_otp_error'
        );

        if (errorType === TOO_MANY_REQUESTS_ERROR) {
          errorMessage = toI18n(
            'authentication.errors.resend_too_many_requests'
          );
        }

        setAlertMessage(errorMessage);
        onError(errorMessage);
      });
  }, [stytchClient, onError, phone]);

  const handleResendCode = useCallback(
    () => stytchClient.sendMfaSmsOtp(phone),
    [phone, stytchClient]
  );

  useEffect(() => {
    if (!user || user.phone_numbers.length === 0) {
      browserHistory.push(SIGN_IN_PATH);
      return;
    }

    sendCode();

    trackUx(EVENT_ACTIONS.PAGE_VIEWED, TRACK_ACTION_TYPES.VIEW);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSuccess = useCallback(() => {
    window.location.href = ROOT_PATH;
    onSuccess();
  }, [onSuccess]);

  return (
    <CodeForm
      initialAlertMessage={alertMessage}
      phoneNumber={phone}
      methodId={phoneId}
      onSetMethodId={setPhoneId}
      onSuccess={handleSuccess}
      onResendCode={handleResendCode}
      onError={onError}
    />
  );
};

export default MfaForm;
